import {makeAutoObservable, runInAction} from "mobx";
import { getStore, useStore } from "../index";
import axios from "axios";


/**
 * 유저 검색 파라미터
 * @typedef {object} UserSearchMap
 * @property {string} searchType - 검색 타입
 * @property {string} searchString - 검색어
 * @property {number} offset - 페이지 오프셋
 * @property {number} limit - 페이지당 개수
 */


/**
 * 대시보드 카운트 정보
 * @typedef {object} DashboardCount
 * @property {number} [userCount] - 회원 수
 * @property {number} [contentCount] - 컨텐츠 수
 * @property {number} [popupCount] - 팝업 수
 * @property {number} [bannerCount] - 배너 수
 */


/**
 * 유저 정보 데이터
 * @typedef {object} UserItem
 * @property {string} [_id] - 유저 고유 ID
 * @property {string} [userId] - 로그인 ID
 * @property {string} [name] - 회원명
 * @property {string} [email] - 이메일
 * @property {string} [createdAt] - 가입일
 */


/**
 * 관리자 운영 유저 관리 Store
 */
export class UserStore {
    /**
     * 공통 검색 Store
     * @type {Object}
     */
    searchBaseStore;

    /**
     * 유저 검색 파라미터
     * @type {UserSearchMap}
     */
    userSearchMap= {
        searchType: "",
        searchString: "",
        offset: 0,
        limit: 10,
    }

    /**
     * 유저 리스트
     * @type {Array<UserItem>}
     */
    userList = [];

    /**
     * 유저 전체 개수
     * @type {number}
     */
    userListCount = 0;

    /**
     * 회원가입 ID 중복 체크 여부
     * @type {boolean}
     */
    authIdChk = false;

    /**
     * 회원가입 ID 중복 체크 완료 여부
     * @type {boolean}
     */
    authIdChkConfirm = false;

    /**
     * Footer 개인정보처리방침
     * @type {string}
     */
    footerPrivacy = "";

    /**
     * Footer 이용약관
     * @type {string}
     */
    footerRule = "";

    /**
     * Footer 수정 상태
     * @type {boolean}
     */
    footerModifyState = false;

    /**
     * 검색 상태 여부
     * @type {boolean}
     */
    searchState = false;

    /**
     * 대시보드 카운트 데이터
     * @type {DashboardCount}
     */
    dashboardCount = {};


    constructor(store) {
        this.store = store;
        this.searchBaseStore = store.searchBaseStore;
        makeAutoObservable(this, {}, { autoBind: true });
    }

    /**
     * 로그인 요청
     * @param {Object} data - 로그인 데이터
     * @returns {Promise<any>}
     */
    async setUser(data) {
        return await axios.post("/api/auth/signIn", data);
    }

    /**
     * 세션 쿠키 저장
     * @param {string} token - 인증 토큰
     * @returns {Promise<void>}
     */
    async setCookie(token) {
        await fetch("/api/session", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ token }),
            credentials: "include",
        });
    }

    /**
     * 클라이언트 로그아웃
     * @returns {Promise<void|any>}
     */
    async setLogoutClientOnly() {
        if (typeof window === "undefined") return;
        await axios.post("/api/auth/signOut");
    }

    /**
     * 세션 쿠키 삭제
     * @returns {Promise<void>}
     */
    async setClearCookie() {
        await fetch("/api/session/logout", {
            method: "POST",
            credentials: "include",
            cache: "no-store",
        });
    }

    /**
     * 클라이언트 상태 초기화
     */
    clearClientState() {
        try {
            localStorage.removeItem("woongbee_info");
        } catch {}
        delete axios.defaults.headers.Authorization;
        delete axios.defaults.headers.authorization;
        delete axios.defaults.headers.common?.Authorization;
        delete axios.defaults.headers.common?.authorization;
    }

    /**
     * 유저 리스트 조회
     * @param {string} params - Query String
     * @returns {Promise<void>}
     */
    async setFetchUserList(params) {
        const search = new URLSearchParams(params.startsWith("?") ? params.slice(1) : params);
        const finalParams = `?${search.toString()}`;
        const response = await axios.get(`/api/auth/users${finalParams}`);
        if(response){
            this.userList = response.data.data.users;
            this.userListCount = response.data.data.count;
        }
    }

    /**
     * 선택 유저 삭제
     * @returns {Promise<any>|undefined}
     */
    async setDeleteUser() {
        if(this.userList && this.userList.length > 0){
            if (!this.searchBaseStore.checkedMap["userListRow"] || this.searchBaseStore.checkedMap["userListRow"].length === 0) return;
            const selectedItems = this.searchBaseStore.checkedMap["userListRow"].map(index => this.userList[index]);
            const items = selectedItems.map(item => item._id);
            const payLoad = {
                userIds: items,
            }
            return await axios.delete("/api/auth/delete", {data: payLoad});
        }
    }

    /**
     * 유저 상태 초기화
     */
    setInitUser (){
        this.userSearchMap = {
            searchType: "",
            searchString: "",
            offset: 0,
            limit: 10,
        }
        this.userList = [];
        this.userListCount = 0;
        this.searchState = false;
    }

    /**
     * 회원가입 ID 중복 체크
     * @param {string} id - 회원 ID
     * @returns {Promise<any>}
     */
    async setSignUpChk(id) {
        return await axios.get(`/api/auth/check?userId=${id}`)
    }

    /**
     * 회원가입 ID 체크 상태 변경
     * @param {boolean} type - 체크 여부
     */
    setSignUpIdChk(type){
        this.authIdChk = type;
    }

    /**
     * 회원가입 ID 체크 완료 상태 변경
     * @param {boolean} type - 체크 완료 여부
     */
    setSignUpIdChkConfirm(type){
        this.authIdChkConfirm = type;
    }

    /**
     * 회원가입 요청
     * @param {Object} data - 회원가입 데이터
     * @returns {Promise<any>}
     */
    async setUserSignUp (data) {
        return await axios.post(`/api/auth/register`, data)
    }

    /**
     * 회원가입 상태 초기화
     */
    setSignUpInit() {
        this.authIdChk = false;
        this.authIdChkConfirm = false;
    }

    /**
     * Footer 데이터 조회
     * @returns {Promise<void>}
     */
    async getFooterData() {
        const response = await axios.get("/api/footer");
        if(response){
            this.footerPrivacy = response.data.data.footer.privacy;
            this.footerRule = response.data.data.footer.rule;
        }
    }

    /**
     * Footer 데이터 저장
     * @returns {Promise<any>}
     */
    async setFooterData() {
        const payLoad = {
            data: {
                privacy: this.footerPrivacy,
                rule: this.footerRule,
            }
        }
        return await axios.post("/api/footer", payLoad);
    }

    /**
     * Footer 수정 상태 변경
     * @param {boolean} value - 수정 여부
     */
    setModifyFooter(value){
        this.footerModifyState = value;
    }

    /**
     * Footer 내부 데이터 변경
     * @param {string} type - 변경 타입
     * @param {string} value - 변경 값
     */
    setFooterInnerData(type, value) {
        if(type === "privacy"){
            this.footerPrivacy = value;
        }
        if(type === "rule"){
            this.footerRule = value;
        }
    }

    /**
     * Footer 상태 초기화
     */
    setInitFooter(){
        this.footerPrivacy = "";
        this.footerRule = "";
        this.footerModifyState = false
    }

    /**
     * Footer 수정 상태 초기화
     */
    setInitFooterState() {
        this.footerModifyState = false;
    }

    /**
     * 내 정보 수정
     * @param {Object} data - 수정 데이터
     * @returns {Promise<any>}
     */
    async setUserMyModify(data) {
        return await axios.post("api/auth/userInfo", data)
    }

    /**
     * 대시보드 데이터 조회
     * @returns {Promise<void>}
     */
    async getDashBoard() {
        const response = await axios.get("/api/content/dashboard");
        runInAction(() => {
            this.dashboardCount = response.data.data.count
        });
    }

    /**
     * 검색 파라미터 초기화
     * @param {Object} param - 초기화 데이터
     */
    clearParams(param) {
        this.searchBaseStore.searchParams = param;
    }
}

/**
 * UserStore Hook
 * @returns {UserStore}
 */
export const useUserStore = () => {
    return useStore().userStore;
}

/**
 * UserStore Getter
 * @returns {UserStore}
 */
export const getUserStore = () => {
    return getStore().userStore;
}
